/**
 * pmsDemoData — single source of truth for PMS Demo V1.
 *
 * Every PMS screen (dashboard, calendar, reservations, reservation detail,
 * allocation, housekeeping, investor preview) derives its numbers from this
 * module. Selectors live in `src/lib/pmsSelectors.js` — never duplicate
 * arrays per screen.
 *
 * Demo period: August 2028. `DEMO_TODAY_ISO` is fixed so arrivals/departures
 * are always visible. The month is hand-tuned so every screen cuadra; the
 * authoritative totals are whatever `monthlyMetrics()` computes from this
 * data, which today is:
 *
 *   commercial_availability = 310 villa-nights − 23 blocked = 287
 *   guest_nights_august      = 202
 *   commercial_occupancy_%   = 202 / 287 ≈ 70.4 %
 *
 * (Blocked nights count the block's `end_date` as occupied, which is how
 * `monthlyMetrics` reads them — the per-block `nights` field is descriptive.)
 *
 * NOTHING here talks to any backend or external API.
 */

// ---------------------------------------------------------------------------
// Metadata
// ---------------------------------------------------------------------------

export const DEMO_TODAY_ISO = "2028-08-21";
export const DEMO_MONTH = "2028-08";
export const DEMO_MONTH_LABEL = "Agosto 2028";
export const DEMO_CURRENCY = "EUR";

/**
 * Tipo de cambio demo IDR/EUR. Usado para convertir el componente fijo del
 * pricing de DOKU (Rp2.000 por transacción con tarjeta) a EUR cuando el
 * investor ve el desglose. Es una **hipótesis** operativa, no un tipo de
 * cambio oficial; centralizado aquí para no dispersar constantes.
 */
export const DEMO_IDR_PER_EUR = 17500;

/**
 * Pricing demo asumido para la pasarela DOKU sobre transacciones con
 * tarjeta (Visa/Mastercard). Basado en el pricing público actual de DOKU;
 * **no es un contrato futuro cerrado** — la pasarela definitiva sigue TBD.
 * OTA reservations no pasan por nuestra pasarela y por tanto no incurren
 * en este coste.
 */
export const DEMO_DOKU_CARD = {
  percentage: 0.028,      // 2,8 % del importe procesado
  fixed_idr: 2000,        // Rp2.000 por transacción exitosa
};

/**
 * Operating expenses (opex) DEMO — importes ESTIMADOS del mes en curso
 * por villa. Son variables cada mes (no un porcentaje fijo del revenue);
 * en el modelo real se conocerán al cierre mensual y viajarán al
 * `monthly_villa_statement`. Aquí sólo modelamos AGOSTO 2028 para no
 * inventar datos sin base.
 *
 * Nota: no es una regla porcentual — son cifras discretas hand-tuned para
 * un 3-bed Vistas y 4-bed Privè operado en Lombok (housekeeping,
 * utilities, pool, gardening, amenities, on-site staff share).
 */
export const OPERATING_EXPENSES_ESTIMATED = {
  "2028-08": {
    V01: 680,   // 3-bed Vistas
    V02: 650,
    V03: 700,
    V04: 710,
    V05: 820,   // +bomba de piscina (I2028001) elevó opex este mes
    V06: 660,
    V07: 920,   // 4-bed Privè, opex más alto por tamaño / staff
    V08: 940,
    V09: 910,
    V10: 930,
  },
};

/** Locked banner shown on every PMS surface. */
export const DEMO_BANNER = {
  title: "Vista previa · Datos de demostración",
  subtitle:
    "Simulación operativa con periodo de Agosto 2028. El complejo real entra en operación en Julio 2027.",
};

// ---------------------------------------------------------------------------
// Pools & villas
// ---------------------------------------------------------------------------

export const POOLS = [
  { pool_id: "vistas", label: "Dolce Vistas", accent: "#9C5236", villa_count: 6 },
  { pool_id: "prive",  label: "Dolce Privè",  accent: "#3C3A55", villa_count: 4 },
];

/** Two-letter code + display name kept short so calendar rows stay compact. */
export const VILLAS = [
  { villa_id: "V01", name: "Villa Ombak",    pool_id: "vistas", bedrooms: 3, sleeps: 6, base_nightly_eur: 200 },
  { villa_id: "V02", name: "Villa Purnama",  pool_id: "vistas", bedrooms: 3, sleeps: 6, base_nightly_eur: 190 },
  { villa_id: "V03", name: "Villa Bintang",  pool_id: "vistas", bedrooms: 3, sleeps: 6, base_nightly_eur: 200 },
  { villa_id: "V04", name: "Villa Cahaya",   pool_id: "vistas", bedrooms: 3, sleeps: 6, base_nightly_eur: 210 },
  { villa_id: "V05", name: "Villa Melati",   pool_id: "vistas", bedrooms: 3, sleeps: 6, base_nightly_eur: 190 },
  { villa_id: "V06", name: "Villa Anggrek",  pool_id: "vistas", bedrooms: 3, sleeps: 6, base_nightly_eur: 205 },
  { villa_id: "V07", name: "Villa Cempaka",  pool_id: "prive",  bedrooms: 4, sleeps: 8, base_nightly_eur: 260 },
  { villa_id: "V08", name: "Villa Kenanga",  pool_id: "prive",  bedrooms: 4, sleeps: 8, base_nightly_eur: 260 },
  { villa_id: "V09", name: "Villa Seruni",   pool_id: "prive",  bedrooms: 4, sleeps: 8, base_nightly_eur: 255 },
  { villa_id: "V10", name: "Villa Tanjung",  pool_id: "prive",  bedrooms: 4, sleeps: 8, base_nightly_eur: 260 },
];

// ---------------------------------------------------------------------------
// Channels (demo — no real integration)
// ---------------------------------------------------------------------------

/** commission_pct is applied to accommodation_revenue for the demo P&L. */
export const CHANNELS = {
  booking:    { key: "booking",    label: "Booking.com",       commission_pct: 0.18, kind: "ota" },
  airbnb:     { key: "airbnb",     label: "Airbnb",             commission_pct: 0.03, kind: "ota" },
  direct_web: { key: "direct_web", label: "Web propia",         commission_pct: 0.05, kind: "direct" },
  manual:     { key: "manual",     label: "Reserva manual",     commission_pct: 0,    kind: "direct" },
  whatsapp:   { key: "whatsapp",   label: "WhatsApp",           commission_pct: 0,    kind: "direct" },
  walk_in:    { key: "walk_in",    label: "Walk-in",            commission_pct: 0,    kind: "direct" },
};

// ---------------------------------------------------------------------------
// Special periods (owner-stay blackout, MotoGP, etc.)
// ---------------------------------------------------------------------------

export const SPECIAL_PERIODS = [
  {
    period_id: "motogp_2028",
    label: "MotoGP Mandalika 2028",
    start_date: "2028-10-15",
    end_date: "2028-10-22",
    blocks: ["OWNER_STAY"],
    note: "Periodo blackout para owner stays por alta demanda.",
  },
];

// ---------------------------------------------------------------------------
// Reservations — 30 confirmed + 1 cancelled = 31 total for August 2028
// ---------------------------------------------------------------------------

/**
 * Deterministic allocation summary shown in the reservation detail and the
 * allocation screen. `recommended_villa_id === assigned_villa_id` means the
 * automatic recommendation was accepted; when they differ, `manual_override`
 * carries the reason and actor.
 */
function autoAlloc(recommended, score = 0.87) {
  return {
    recommended_villa_id: recommended,
    assigned_villa_id: recommended,
    mode: "automatic",
    fairness_score: score,
    weights: { revenue: 0.7, occupancy: 0.3, current_month: 0.7, ytd: 0.3 },
    candidates: null,
    manual_override: null,
    calculated_at: null,
  };
}

function guest(name, adults = 2, children = 0, country = "ES") {
  return { name, adults, children, country };
}

// Reservations declared in villa-ordered blocks for readability. Order does
// not affect selectors.
export const RESERVATIONS = [
  // V01 — Villa Ombak (Vistas)
  { reservation_id: "R2028001", channel: "booking",    pool_id: "vistas", assigned_villa_id: "V01",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-01", check_out: "2028-08-08", nights: 7,
    guest: guest("Familia Rossi", 2, 2, "IT"),
    accommodation_revenue_eur: 1400, currency: "EUR",
    allocation: autoAlloc("V01", 0.82),
    created_at: "2028-06-14T09:12:00Z" },
  { reservation_id: "R2028002", channel: "airbnb",     pool_id: "vistas", assigned_villa_id: "V01",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-10", check_out: "2028-08-17", nights: 7,
    guest: guest("Anders Nielsen", 2, 0, "DK"),
    accommodation_revenue_eur: 1330, currency: "EUR",
    allocation: autoAlloc("V01", 0.79),
    created_at: "2028-07-02T18:41:00Z" },
  { reservation_id: "R2028003", channel: "direct_web", payment_method: "card", pool_id: "vistas", assigned_villa_id: "V01",
    booking_status: "confirmed", stay_status: "checked_in", payment_status: "paid",
    check_in: "2028-08-19", check_out: "2028-08-26", nights: 7,
    guest: guest("Sara Lindqvist", 2, 1, "SE"),
    accommodation_revenue_eur: 1470, currency: "EUR",
    allocation: autoAlloc("V01", 0.86),
    created_at: "2028-07-24T11:00:00Z" },

  // V02 — Villa Purnama (Vistas)
  { reservation_id: "R2028004", channel: "direct_web", payment_method: "card", pool_id: "vistas", assigned_villa_id: "V02",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-01", check_out: "2028-08-06", nights: 5,
    guest: guest("Marcus Weber", 2, 0, "DE"),
    accommodation_revenue_eur: 950, currency: "EUR",
    allocation: autoAlloc("V02", 0.81),
    created_at: "2028-06-01T15:20:00Z" },
  { reservation_id: "R2028005", channel: "booking",    pool_id: "vistas", assigned_villa_id: "V02",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-14", check_out: "2028-08-21", nights: 7,
    guest: guest("Julien Martin", 2, 2, "FR"),
    accommodation_revenue_eur: 1330, currency: "EUR",
    allocation: autoAlloc("V02", 0.83),
    created_at: "2028-07-05T08:00:00Z" },
  // Llega el mismo día en que sale R2028005: es el único turnaround del mismo
  // día del dataset y sin él la rama de prioridad alta de
  // `housekeepingTasksForDay` nunca se ejercita. Mismas 8 noches y mismos
  // ingresos que antes, así que los totales del mes no se mueven.
  { reservation_id: "R2028006", channel: "airbnb",     pool_id: "vistas", assigned_villa_id: "V02",
    booking_status: "confirmed", stay_status: "pre_arrival", payment_status: "authorized",
    check_in: "2028-08-21", check_out: "2028-08-29", nights: 8,
    guest: guest("Emma van Dijk", 2, 0, "NL"),
    accommodation_revenue_eur: 1520, currency: "EUR",
    allocation: autoAlloc("V02", 0.84),
    created_at: "2028-08-01T14:33:00Z" },

  // V03 — Villa Bintang (Vistas)
  { reservation_id: "R2028007", channel: "direct_web", payment_method: "card", pool_id: "vistas", assigned_villa_id: "V03",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-03", check_out: "2028-08-10", nights: 7,
    guest: guest("Luca Bianchi", 4, 0, "IT"),
    accommodation_revenue_eur: 1400, currency: "EUR",
    allocation: autoAlloc("V03", 0.80),
    created_at: "2028-06-22T10:12:00Z" },
  { reservation_id: "R2028008", channel: "airbnb",     pool_id: "vistas", assigned_villa_id: "V03",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-12", check_out: "2028-08-19", nights: 7,
    guest: guest("Rebecca Cohen", 2, 2, "GB"),
    accommodation_revenue_eur: 1400, currency: "EUR",
    allocation: autoAlloc("V03", 0.82),
    created_at: "2028-07-11T19:22:00Z" },
  { reservation_id: "R2028009", channel: "booking",    pool_id: "vistas", assigned_villa_id: "V03",
    booking_status: "confirmed", stay_status: "pre_arrival", payment_status: "paid",
    check_in: "2028-08-21", check_out: "2028-08-28", nights: 7,
    guest: guest("Carla Moreno", 2, 1, "ES"),
    accommodation_revenue_eur: 1470, currency: "EUR",
    allocation: {
      recommended_villa_id: "V05",
      assigned_villa_id: "V03",
      mode: "manual_override",
      fairness_score: 0.71,
      weights: { revenue: 0.7, occupancy: 0.3, current_month: 0.7, ytd: 0.3 },
      candidates: null,
      manual_override: {
        reason: "Huéspedes recurrentes que solicitaron V03 explícitamente.",
        actor_user_id: "user_admin_001",
        actor_name: "Dolce Admin",
        at: "2028-07-30T09:15:00Z",
      },
      calculated_at: "2028-07-30T09:12:00Z",
    },
    created_at: "2028-07-30T09:00:00Z" },

  // V04 — Villa Cahaya (Vistas)
  { reservation_id: "R2028010", channel: "whatsapp", payment_method: "card",   pool_id: "vistas", assigned_villa_id: "V04",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-02", check_out: "2028-08-09", nights: 7,
    guest: guest("Pieter Jansen", 2, 3, "NL"),
    accommodation_revenue_eur: 1470, currency: "EUR",
    allocation: autoAlloc("V04", 0.85),
    created_at: "2028-06-18T12:00:00Z" },
  { reservation_id: "R2028011", channel: "booking",    pool_id: "vistas", assigned_villa_id: "V04",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-14", check_out: "2028-08-21", nights: 7,
    guest: guest("Sophia Reinhardt", 4, 0, "DE"),
    accommodation_revenue_eur: 1470, currency: "EUR",
    allocation: autoAlloc("V04", 0.83),
    created_at: "2028-07-08T14:22:00Z" },
  { reservation_id: "R2028012", channel: "direct_web", payment_method: "card", pool_id: "vistas", assigned_villa_id: "V04",
    booking_status: "confirmed", stay_status: "pre_arrival", payment_status: "authorized",
    check_in: "2028-08-23", check_out: "2028-08-30", nights: 7,
    guest: guest("Nikolai Petrov", 2, 0, "AT"),
    accommodation_revenue_eur: 1470, currency: "EUR",
    allocation: autoAlloc("V04", 0.84),
    created_at: "2028-08-02T09:30:00Z" },

  // V05 — Villa Melati (Vistas) — 4n maintenance block 08-16→08-20
  { reservation_id: "R2028013", channel: "airbnb",     pool_id: "vistas", assigned_villa_id: "V05",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-01", check_out: "2028-08-07", nights: 6,
    guest: guest("Camille Dubois", 2, 0, "FR"),
    accommodation_revenue_eur: 1140, currency: "EUR",
    allocation: autoAlloc("V05", 0.78),
    created_at: "2028-06-05T11:45:00Z" },
  { reservation_id: "R2028014", channel: "manual", payment_method: "bank_transfer",     pool_id: "vistas", assigned_villa_id: "V05",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-09", check_out: "2028-08-15", nights: 6,
    guest: guest("David Cohen", 2, 2, "IL"),
    accommodation_revenue_eur: 1080, currency: "EUR",
    allocation: autoAlloc("V05", 0.79),
    created_at: "2028-06-30T08:22:00Z" },
  { reservation_id: "R2028015", channel: "booking",    pool_id: "vistas", assigned_villa_id: "V05",
    booking_status: "confirmed", stay_status: "pre_arrival", payment_status: "paid",
    check_in: "2028-08-21", check_out: "2028-08-30", nights: 9,
    guest: guest("Familia Fischer", 2, 3, "CH"),
    accommodation_revenue_eur: 1800, currency: "EUR",
    allocation: autoAlloc("V05", 0.88),
    created_at: "2028-07-19T16:12:00Z" },

  // V06 — Villa Anggrek (Vistas) — owner stay 08-15→08-22
  { reservation_id: "R2028016", channel: "direct_web", payment_method: "card", pool_id: "vistas", assigned_villa_id: "V06",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-01", check_out: "2028-08-04", nights: 3,
    guest: guest("Andrea Ricci", 2, 0, "IT"),
    accommodation_revenue_eur: 615, currency: "EUR",
    allocation: autoAlloc("V06", 0.76),
    created_at: "2028-06-10T09:00:00Z" },
  { reservation_id: "R2028017", channel: "airbnb",     pool_id: "vistas", assigned_villa_id: "V06",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-06", check_out: "2028-08-13", nights: 7,
    guest: guest("Katrin Meier", 2, 2, "DE"),
    accommodation_revenue_eur: 1435, currency: "EUR",
    allocation: autoAlloc("V06", 0.80),
    created_at: "2028-06-28T12:45:00Z" },
  { reservation_id: "R2028018", channel: "direct_web", payment_method: "card", pool_id: "vistas", assigned_villa_id: "V06",
    booking_status: "confirmed", stay_status: "pre_arrival", payment_status: "authorized",
    check_in: "2028-08-24", check_out: "2028-08-31", nights: 7,
    guest: guest("Chloe Wilson", 2, 1, "GB"),
    accommodation_revenue_eur: 1470, currency: "EUR",
    allocation: autoAlloc("V06", 0.83),
    created_at: "2028-08-05T10:00:00Z" },

  // V07 — Villa Cempaka (Privè)
  { reservation_id: "R2028019", channel: "direct_web", payment_method: "card", pool_id: "prive", assigned_villa_id: "V07",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-01", check_out: "2028-08-08", nights: 7,
    guest: guest("Alessandra Ferrari", 4, 2, "IT"),
    accommodation_revenue_eur: 1820, currency: "EUR",
    allocation: autoAlloc("V07", 0.84),
    created_at: "2028-05-22T10:30:00Z" },
  { reservation_id: "R2028020", channel: "booking",    pool_id: "prive", assigned_villa_id: "V07",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-10", check_out: "2028-08-17", nights: 7,
    guest: guest("Familia Chen", 2, 3, "SG"),
    accommodation_revenue_eur: 1820, currency: "EUR",
    allocation: autoAlloc("V07", 0.82),
    created_at: "2028-06-19T14:00:00Z" },
  { reservation_id: "R2028021", channel: "airbnb",     pool_id: "prive", assigned_villa_id: "V07",
    booking_status: "confirmed", stay_status: "checked_in", payment_status: "paid",
    check_in: "2028-08-19", check_out: "2028-08-26", nights: 7,
    guest: guest("Familia Novak", 4, 3, "CZ"),
    accommodation_revenue_eur: 1820, currency: "EUR",
    allocation: autoAlloc("V07", 0.85),
    created_at: "2028-07-27T09:50:00Z" },
  { reservation_id: "R2028022", channel: "whatsapp", payment_method: "card",   pool_id: "prive", assigned_villa_id: "V07",
    booking_status: "pending",   stay_status: "pre_arrival", payment_status: "pending",
    check_in: "2028-08-28", check_out: "2028-08-31", nights: 3,
    guest: guest("Ravi Menon", 2, 0, "AE"),
    accommodation_revenue_eur: 780, currency: "EUR",
    allocation: autoAlloc("V07", 0.79),
    created_at: "2028-08-15T08:10:00Z" },

  // V08 — Villa Kenanga (Privè) — owner stay 08-12→08-19
  { reservation_id: "R2028023", channel: "airbnb",     pool_id: "prive", assigned_villa_id: "V08",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-02", check_out: "2028-08-10", nights: 8,
    guest: guest("Emily Carter", 4, 2, "GB"),
    accommodation_revenue_eur: 2080, currency: "EUR",
    allocation: autoAlloc("V08", 0.81),
    created_at: "2028-06-03T17:00:00Z" },
  { reservation_id: "R2028024", channel: "booking",    pool_id: "prive", assigned_villa_id: "V08",
    booking_status: "confirmed", stay_status: "pre_arrival", payment_status: "paid",
    check_in: "2028-08-21", check_out: "2028-08-30", nights: 9,
    guest: guest("Familia Papadakis", 4, 3, "GR"),
    accommodation_revenue_eur: 2340, currency: "EUR",
    allocation: autoAlloc("V08", 0.87),
    created_at: "2028-07-14T11:00:00Z" },

  // V09 — Villa Seruni (Privè)
  { reservation_id: "R2028025", channel: "booking",    pool_id: "prive", assigned_villa_id: "V09",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-01", check_out: "2028-08-09", nights: 8,
    guest: guest("Familia Silva", 4, 2, "PT"),
    accommodation_revenue_eur: 2040, currency: "EUR",
    allocation: autoAlloc("V09", 0.82),
    created_at: "2028-05-30T08:30:00Z" },
  { reservation_id: "R2028026", channel: "whatsapp", payment_method: "card",   pool_id: "prive", assigned_villa_id: "V09",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-11", check_out: "2028-08-18", nights: 7,
    guest: guest("Familia Kobayashi", 2, 3, "JP"),
    accommodation_revenue_eur: 1785, currency: "EUR",
    allocation: autoAlloc("V09", 0.80),
    created_at: "2028-06-25T09:22:00Z" },
  { reservation_id: "R2028027", channel: "direct_web", payment_method: "card", pool_id: "prive", assigned_villa_id: "V09",
    booking_status: "confirmed", stay_status: "checked_in", payment_status: "paid",
    check_in: "2028-08-20", check_out: "2028-08-27", nights: 7,
    guest: guest("Isabella Rossi", 4, 0, "IT"),
    accommodation_revenue_eur: 1820, currency: "EUR",
    allocation: autoAlloc("V09", 0.85),
    created_at: "2028-07-22T14:00:00Z" },

  // V10 — Villa Tanjung (Privè) — includes cancelled R2028029c
  { reservation_id: "R2028028", channel: "direct_web", payment_method: "card", pool_id: "prive", assigned_villa_id: "V10",
    booking_status: "confirmed", stay_status: "checked_out", payment_status: "paid",
    check_in: "2028-08-02", check_out: "2028-08-08", nights: 6,
    guest: guest("Kenji Sato", 2, 0, "JP"),
    accommodation_revenue_eur: 1560, currency: "EUR",
    allocation: autoAlloc("V10", 0.79),
    created_at: "2028-06-09T13:10:00Z" },
  { reservation_id: "R2028029c", channel: "airbnb",    pool_id: "prive", assigned_villa_id: "V10",
    booking_status: "cancelled", stay_status: "pre_arrival", payment_status: "partially_refunded",
    check_in: "2028-08-10", check_out: "2028-08-14", nights: 4,
    guest: guest("James O'Connor", 2, 0, "IE"),
    accommodation_revenue_eur: 1040, currency: "EUR",
    cancellation: {
      cancelled_at: "2028-08-01T10:00:00Z",
      cancelled_by: "guest",
      retained_revenue_eur: 520,
      refunded_eur: 520,
    },
    allocation: autoAlloc("V10", 0.72),
    created_at: "2028-06-15T09:00:00Z" },
  { reservation_id: "R2028030", channel: "booking",    pool_id: "prive", assigned_villa_id: "V10",
    booking_status: "confirmed", stay_status: "checked_in", payment_status: "paid",
    check_in: "2028-08-16", check_out: "2028-08-24", nights: 8,
    guest: guest("Familia Andersson", 4, 2, "SE"),
    accommodation_revenue_eur: 2080, currency: "EUR",
    allocation: autoAlloc("V10", 0.83),
    created_at: "2028-07-12T15:30:00Z" },
  { reservation_id: "R2028031", channel: "manual", payment_method: "bank_transfer",     pool_id: "prive", assigned_villa_id: "V10",
    booking_status: "confirmed", stay_status: "pre_arrival", payment_status: "partially_paid",
    check_in: "2028-08-27", check_out: "2028-08-31", nights: 4,
    guest: guest("Lucas Almeida", 2, 1, "BR"),
    accommodation_revenue_eur: 1040, currency: "EUR",
    allocation: autoAlloc("V10", 0.81),
    created_at: "2028-08-10T12:00:00Z" },
];

// ---------------------------------------------------------------------------
// Availability blocks (owner stays / maintenance / admin blocks)
// ---------------------------------------------------------------------------

export const AVAILABILITY_BLOCKS = [
  {
    block_id: "B2028001",
    villa_id: "V05",
    type: "MAINTENANCE",
    start_date: "2028-08-16",
    end_date: "2028-08-20",
    nights: 4,
    fairness_compensable: true,
    reason: "Sustitución de bomba de piscina — proveedor externo.",
    created_by: "user_admin_001",
    created_at: "2028-08-12T17:00:00Z",
    status: "resolved",
  },
  {
    block_id: "B2028002",
    villa_id: "V06",
    type: "OWNER_STAY",
    start_date: "2028-08-15",
    end_date: "2028-08-22",
    nights: 7,
    fairness_compensable: false,
    reason: "Estancia propietario · aprobada con 34 días de antelación.",
    created_by: "user_admin_001",
    created_at: "2028-07-12T09:30:00Z",
    status: "in_progress",
    owner_stay_request_id: "OS2028002",
  },
  {
    block_id: "B2028003",
    villa_id: "V08",
    type: "OWNER_STAY",
    start_date: "2028-08-12",
    end_date: "2028-08-19",
    nights: 7,
    fairness_compensable: false,
    reason: "Estancia propietario · aprobada con 45 días de antelación.",
    created_by: "user_admin_001",
    created_at: "2028-06-28T14:00:00Z",
    status: "completed",
    owner_stay_request_id: "OS2028003",
  },
  {
    // Bloqueo vivo HOY: es lo que hace que V04 esté realmente fuera de
    // servicio y no solo "marcada en rojo". Acaba el 22 para no pisar la
    // llegada del 23 (R2028012).
    block_id: "B2028004",
    villa_id: "V04",
    type: "MAINTENANCE",
    start_date: "2028-08-21",
    end_date: "2028-08-22",
    nights: 2,
    fairness_compensable: true,
    reason: "Aire acondicionado principal averiado — pendiente de reparación.",
    created_by: "user_hk_01",
    created_at: "2028-08-21T13:25:00Z",
    status: "active",
    issue_id: "I2028007",
  },
];

// ---------------------------------------------------------------------------
// Owner stay requests (documented for future OwnerStays screen)
// ---------------------------------------------------------------------------

export const OWNER_STAY_REQUESTS = [
  {
    request_id: "OS2028002",
    villa_id: "V06",
    investor_user_id: "inv_v06",
    investor_name: "Familia Bernal",
    start_date: "2028-08-15",
    end_date: "2028-08-22",
    nights: 7,
    status: "approved",
    requested_at: "2028-07-12T09:00:00Z",
    approved_at: "2028-07-12T09:30:00Z",
    approved_by: "user_admin_001",
  },
  {
    request_id: "OS2028003",
    villa_id: "V08",
    investor_user_id: "inv_v08",
    investor_name: "Familia Ono",
    start_date: "2028-08-12",
    end_date: "2028-08-19",
    nights: 7,
    status: "completed",
    requested_at: "2028-06-28T13:30:00Z",
    approved_at: "2028-06-28T14:00:00Z",
    approved_by: "user_admin_001",
  },
  {
    request_id: "OS2028004",
    villa_id: "V01",
    investor_user_id: "inv_v01",
    investor_name: "Elena Ruiz",
    start_date: "2028-10-05",
    end_date: "2028-10-12",
    nights: 7,
    status: "requested",
    requested_at: "2028-08-15T10:00:00Z",
    approved_at: null,
    approved_by: null,
    note: "Solicitud puntual · sin conflicto detectado en dataset actual.",
  },
  {
    request_id: "OS2028005",
    villa_id: "V02",
    investor_user_id: "inv_v02",
    investor_name: "Marc Feliu",
    start_date: "2028-10-15",
    end_date: "2028-10-22",
    nights: 7,
    status: "requested",
    requested_at: "2028-08-18T14:00:00Z",
    approved_at: null,
    approved_by: null,
    note: "Coincide con semana MotoGP · bloqueada para owner stays.",
  },
  {
    request_id: "OS2028006",
    villa_id: "V04",
    investor_user_id: "inv_v04",
    investor_name: "Familia Gómez",
    start_date: "2028-08-30",
    end_date: "2028-09-05",
    nights: 6,
    status: "requested",
    requested_at: "2028-08-20T08:00:00Z",
    approved_at: null,
    approved_by: null,
    note: "Solicitud con solo 10 días de antelación · fuera de política.",
  },
  {
    request_id: "OS2028007",
    villa_id: "V07",
    investor_user_id: "inv_v07",
    investor_name: "Familia Prasetya",
    start_date: "2028-09-15",
    end_date: "2028-09-22",
    nights: 7,
    status: "rejected",
    requested_at: "2028-08-05T09:00:00Z",
    approved_at: "2028-08-06T10:00:00Z",
    approved_by: "user_admin_001",
    rejection_reason: "Villa reservada durante todo septiembre · no desplazable.",
  },
  {
    request_id: "OS2028008",
    villa_id: "V03",
    investor_user_id: "inv_v03",
    investor_name: "David Ors",
    start_date: "2028-11-10",
    end_date: "2028-11-17",
    nights: 7,
    status: "requested",
    requested_at: "2028-08-21T07:00:00Z",
    approved_at: null,
    approved_by: null,
    note: "Solicitud reciente · pendiente de revisión.",
  },
];

// ---------------------------------------------------------------------------
// Property issues (Checkpoint B — Issues/Maintenance surface)
// ---------------------------------------------------------------------------

/**
 * Coherencia con blocks:
 *   - I2028001 → B2028001 (V05, bomba de piscina, resolved).
 *   - I2028004 (V03) es la incidencia de referencia con la que se justifica
 *     el manual_override de R2028009 (huésped VIP; sin bloqueo de inventario).
 *   - Los assignees y reporters usan ids ficticios de operación.
 */
export const PROPERTY_ISSUES = [
  {
    issue_id: "I2028001",
    villa_id: "V05",
    category: "maintenance",
    priority: "high",
    status: "resolved",
    blocks_inventory: true,
    fairness_compensable: true,
    reservation_id: null,
    linked_block_id: "B2028001",
    reporter: { user_id: "user_mgr_01", name: "Maria Sánchez", role: "manager" },
    assignee: { user_id: "user_maint_02", name: "Wayan Suarta",  role: "maintenance" },
    title: "Bomba de piscina fallida",
    description: "Ruido anormal + baja presión. Sustitución completa con proveedor externo.",
    photos_placeholder: 3,
    reported_at: "2028-08-11T08:12:00Z",
    resolved_at: "2028-08-20T17:30:00Z",
  },
  {
    issue_id: "I2028002",
    villa_id: "V09",
    category: "maintenance",
    priority: "medium",
    status: "in_progress",
    blocks_inventory: false,
    fairness_compensable: false,
    reservation_id: "R2028027",
    linked_block_id: null,
    reporter: { user_id: "user_rec_01", name: "Adi Pratama",    role: "reception" },
    assignee: { user_id: "user_maint_01", name: "Ketut Wisnu",   role: "maintenance" },
    title: "Grifo ducha principal gotea",
    description: "Detectado durante inspección diaria. No bloquea uso; sustitución programada tras salida.",
    photos_placeholder: 1,
    reported_at: "2028-08-20T09:45:00Z",
    resolved_at: null,
  },
  {
    issue_id: "I2028003",
    villa_id: "V10",
    category: "guest",
    priority: "low",
    status: "open",
    blocks_inventory: false,
    fairness_compensable: false,
    reservation_id: "R2028030",
    linked_block_id: null,
    reporter: { user_id: "user_rec_02", name: "Made Sari",       role: "reception" },
    assignee: null,
    title: "Solicitud: cuna adicional",
    description: "Huésped solicita cuna adicional para tercera noche. Pedir a housekeeping.",
    photos_placeholder: 0,
    reported_at: "2028-08-21T06:15:00Z",
    resolved_at: null,
  },
  {
    issue_id: "I2028004",
    villa_id: "V03",
    category: "cleaning",
    priority: "low",
    status: "open",
    blocks_inventory: false,
    fairness_compensable: false,
    reservation_id: null,
    linked_block_id: null,
    reporter: { user_id: "user_hk_01",  name: "Kadek Ratih",     role: "housekeeping" },
    assignee: null,
    title: "Mancha en sofá salón",
    description: "Mancha superficial detectada tras el checkout de R2028008. Limpieza profunda solicitada.",
    photos_placeholder: 2,
    reported_at: "2028-08-19T11:00:00Z",
    resolved_at: null,
  },
  {
    issue_id: "I2028005",
    villa_id: "V07",
    category: "maintenance",
    priority: "medium",
    status: "resolved",
    blocks_inventory: false,
    fairness_compensable: false,
    reservation_id: null,
    linked_block_id: null,
    reporter: { user_id: "user_mgr_01", name: "Maria Sánchez",   role: "manager" },
    assignee: { user_id: "user_maint_02", name: "Wayan Suarta",  role: "maintenance" },
    title: "Servicio anual A/A",
    description: "Servicio preventivo trimestral del aire acondicionado principal.",
    photos_placeholder: 0,
    reported_at: "2028-08-05T09:00:00Z",
    resolved_at: "2028-08-05T15:00:00Z",
  },
  {
    issue_id: "I2028007",
    villa_id: "V04",
    category: "maintenance",
    priority: "urgent",
    status: "assigned",
    blocks_inventory: true,
    fairness_compensable: true,
    reservation_id: null,
    linked_block_id: "B2028004",
    reporter: { user_id: "user_hk_01",  name: "Kadek Ratih",     role: "housekeeping" },
    assignee: { user_id: "user_maint_02", name: "Wayan Suarta",  role: "maintenance" },
    title: "Aire acondicionado principal averiado",
    description: "Detectado en la inspección posterior a la salida: el equipo del salón no enfría. Villa fuera de servicio hasta la reparación.",
    photos_placeholder: 2,
    reported_at: "2028-08-21T13:20:00Z",
    resolved_at: null,
  },
  {
    issue_id: "I2028006",
    villa_id: "V02",
    category: "damage",
    priority: "medium",
    status: "assigned",
    blocks_inventory: false,
    fairness_compensable: false,
    reservation_id: "R2028005",
    linked_block_id: null,
    reporter: { user_id: "user_hk_02",  name: "Nyoman Ayu",      role: "housekeeping" },
    assignee: { user_id: "user_maint_01", name: "Ketut Wisnu",   role: "maintenance" },
    title: "Toallas dañadas · retirar de inventario",
    description: "Set de 4 toallas con manchas persistentes. Cargar a la fianza de R2028005 si procede.",
    photos_placeholder: 2,
    reported_at: "2028-08-21T09:30:00Z",
    resolved_at: null,
  },
];

// ---------------------------------------------------------------------------
// YTD stats — ilustrativos para el fairness horizon (70% mes / 30% YTD)
// ---------------------------------------------------------------------------

/**
 * Acumulado enero–julio 2028 por villa (antes del mes en curso). Aquí no
 * derivamos de reservations porque el dataset demo solo declara Agosto — se
 * exponen como valores hand-tuned para que el fairness ranking tenga una
 * horizontal creíble.
 */
export const YTD_STATS_2028 = {
  V01: { revenue_eur: 18400, guest_nights: 148, owner_nights: 6,  maintenance_nights: 2 },
  V02: { revenue_eur: 17100, guest_nights: 140, owner_nights: 8,  maintenance_nights: 0 },
  V03: { revenue_eur: 19200, guest_nights: 152, owner_nights: 4,  maintenance_nights: 3 },
  V04: { revenue_eur: 18800, guest_nights: 146, owner_nights: 5,  maintenance_nights: 1 },
  V05: { revenue_eur: 15200, guest_nights: 128, owner_nights: 3,  maintenance_nights: 9 },
  V06: { revenue_eur: 16400, guest_nights: 132, owner_nights: 14, maintenance_nights: 0 },
  V07: { revenue_eur: 26500, guest_nights: 138, owner_nights: 4,  maintenance_nights: 2 },
  V08: { revenue_eur: 24000, guest_nights: 124, owner_nights: 12, maintenance_nights: 0 },
  V09: { revenue_eur: 27100, guest_nights: 142, owner_nights: 3,  maintenance_nights: 1 },
  V10: { revenue_eur: 22800, guest_nights: 122, owner_nights: 5,  maintenance_nights: 4 },
};

// ---------------------------------------------------------------------------
// Monthly statements — July 2028 closed; August 2028 in-progress
// ---------------------------------------------------------------------------

/**
 * One statement per villa per month. `status: closed` locks
 * `owner_net_final`; `in_progress` exposes `owner_net_estimated` only.
 *
 * July numbers are illustrative (no reservations declared for July — this
 * is just so investor statements have a "last closed month" reference).
 */
export const MONTHLY_STATEMENTS = [
  // June 2028 (closed) — historical demo statement.
  { statement_id: "S2028V0106", villa_id: "V01", month: "2028-06", status: "closed",
    accommodation_revenue_eur: 3780, tax_eur: 378, channel_cost_eur: 453, operating_expenses_final_eur: 567,
    management_eur: 358, owner_net_final_eur: 2024, owner_net_estimated_eur: null, closed_at: "2028-07-05T09:00:00Z" },
  // July 2028 (closed) — illustrative only.
  { statement_id: "S2028V0107", villa_id: "V01", month: "2028-07", status: "closed",
    accommodation_revenue_eur: 4200, tax_eur: 420, channel_cost_eur: 504, operating_expenses_final_eur: 630,
    management_eur: 397, owner_net_final_eur: 2249, owner_net_estimated_eur: null, closed_at: "2028-08-05T09:00:00Z" },
  { statement_id: "S2028V0207", villa_id: "V02", month: "2028-07", status: "closed",
    accommodation_revenue_eur: 3990, tax_eur: 399, channel_cost_eur: 478, operating_expenses_final_eur: 599,
    management_eur: 377, owner_net_final_eur: 2137, owner_net_estimated_eur: null, closed_at: "2028-08-05T09:00:00Z" },
];

// ---------------------------------------------------------------------------
// UI-facing labels — status → human
// ---------------------------------------------------------------------------

export const BOOKING_STATUS_LABEL = {
  hold:      "Hold",
  pending:   "Pendiente",
  confirmed: "Confirmada",
  cancelled: "Cancelada",
  no_show:   "No-show",
};

export const STAY_STATUS_LABEL = {
  pre_arrival: "Pre-llegada",
  checked_in:  "In-house",
  checked_out: "Salida",
};

export const PAYMENT_STATUS_LABEL = {
  unpaid:             "Sin pagar",
  pending:            "Pendiente",
  authorized:         "Autorizado",
  partially_paid:     "Parcial",
  paid:               "Pagado",
  partially_refunded: "Reembolso parcial",
  refunded:           "Reembolsado",
  failed:             "Fallido",
};

export const VILLA_STATE_LABEL = {
  AVAILABLE:     "Disponible",
  OCCUPIED:      "Ocupada",
  CLEANING:      "Por limpiar",
  OWNER_STAY:    "Owner stay",
  MAINTENANCE:   "Mantenimiento",
  ARRIVAL_TODAY: "Llega hoy",
};
